/**
 * Shared auth-redirect contract for the Hub portal.
 *
 * Closes H3/H4/H5/H8 from the AI Hub audit:
 *  - Persists the deep-link path under a portal-scoped sessionStorage key so
 *    `/callback` can restore it after the cross-origin bounce.
 *  - Uses `window.location.replace` to the auth gateway's `/login` path
 *    (not the bare origin) — the other portals already use `/login`; this
 *    closes the Hub-only divergence.
 *  - Skips the intermediate `/auth` SPA route entirely — the route guard
 *    leaves cross-origin in a single hop.
 *  - Never sets `document.title = "Loading https://..."`.
 */

const STORAGE_KEY = "pulse_return_to:hub";
const AUTH_LOGIN = "https://auth.pulsebusiness.ai/login";
const CALLBACK =
  typeof window !== "undefined"
    ? `${window.location.origin}/callback`
    : "https://aihub.pulsebusiness.ai/callback";

export function persistReturnPath(p: string): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, p);
  } catch {
    /* sessionStorage may be unavailable (private mode, SSR) */
  }
}

export function restoreReturnPath(): string | null {
  try {
    const v = sessionStorage.getItem(STORAGE_KEY);
    sessionStorage.removeItem(STORAGE_KEY);
    return v;
  } catch {
    return null;
  }
}

export function redirectToAuth(currentPath: string): never {
  persistReturnPath(currentPath);
  const url = `${AUTH_LOGIN}?redirect_to=${encodeURIComponent(CALLBACK)}`;
  window.location.replace(url);
  // Previously this threw `Error("unreachable: redirectToAuth")` to honour
  // the `never` return type. The throw propagated through useEffect into the
  // root ErrorBoundary and showed up as a console error during the
  // tear-down frame. Returning `undefined as never` keeps TypeScript happy
  // without producing browser console noise.
  return undefined as never;
}

export const __testing = { STORAGE_KEY, AUTH_LOGIN, CALLBACK };
